// Generates exported Excel file from data stored in Vuex
import { saveAs } from 'file-saver';
import {
  getSurveyQuestion,
} from "./utils.js";

const ExcelJS = require('exceljs');

export function generateExcelExport(vuex, survey1Averages, survey2Averages) {
  // This is for classic
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();
  workbook.creator = "SmoothTeam";

  generatePlayersSheet(workbook, vuex);
  generateCasesSheet(workbook, vuex);
  generateSelfEvaluationsSheet(workbook, vuex);

  generateNominationsSheet(workbook, vuex);

  if (vuex.state.main.game.enableComments && vuex.getters.getIsFacilitator) {
    generateCommentsSheet(workbook, vuex);
  }

  if (vuex.state.main.game.enableSuggestions && vuex.getters.getIsFacilitator) {
    generateSuggestionsSheet(workbook, vuex);
  }

  if (vuex.state.main.game.enableSurvey) {
     generateSurveySheet(workbook, vuex, survey1Averages, survey2Averages);
  }

  // https://github.com/exceljs/exceljs/issues/354#issuecomment-325764873
  workbook.xlsx.writeBuffer().then((data) => {
    const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "SmoothTeam " + vuex.state.main.game.name + ".xlsx")
  });
}

export function generateExcelExportForRPSReport(vuex) {
  // This is for rps
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();
  workbook.creator = "SmoothTeam";
  generatePlayersSheet(workbook, vuex);
  generateRPSCasesSheet(workbook, vuex);
  generateRPSCaseChoices(workbook, vuex);
  generatePlayerChoicesSheet(workbook, vuex);

  if (vuex.state.main.game.enableSuggestions && vuex.getters.getIsFacilitator) {
    generateSuggestionsSheet(workbook, vuex);
  }


  // https://github.com/exceljs/exceljs/issues/354#issuecomment-325764873
  workbook.xlsx.writeBuffer().then((data) => {
    const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "SmoothTeam " + vuex.state.main.game.name + ".xlsx")
  });
}

function generatePlayersSheet(workbook, vuex) {
  const playersSheet = workbook.addWorksheet('Players');

  const players = vuex.getters.getPlayers;

  if (vuex.getters.getIsFacilitator) {
    playersSheet.addRow(["Nick name", "First name", "Last name", "Email"]);
  } else {
    playersSheet.addRow(["Nick name"]);
  }

  players.forEach(player => {
    if (vuex.getters.getIsFacilitator) {
      playersSheet.addRow([player.nick_name, player.first_name, player.last_name, player.email]);
    } else {
      playersSheet.addRow([player.nick_name]);
    }
  })
}

function generateCasesSheet(workbook, vuex) {
  const casesSheet = workbook.addWorksheet('Cases');

  const cases = vuex.getters.getCasesInCurrentLocale;

  casesSheet.addRow(["Case number", "Name"]);

  cases.forEach(aCase => {
    casesSheet.addRow([aCase.caseNumber, aCase.shortName]);
  })
}

function generateRPSCasesSheet(workbook, vuex) {
  const casesSheet = workbook.addWorksheet('Cases');
  const cases = vuex.getters.getRPSCasesInCurrentLocale;
  casesSheet.addRow(["Case number", "Name"]);
  cases.forEach(aCase => {
    casesSheet.addRow([aCase.caseNumber, aCase.caseQuestion]);
  })
}

function generateRPSCaseChoices(workbook, vuex) {
  const choicesSheet = workbook.addWorksheet('Case Choices');

  choicesSheet.addRow(["Case number", "Choice number", "Choice"]);

  for (let caseNumber = 1; caseNumber <= vuex.getters.getNumberOfRPSCases; caseNumber++) {
    const rpsCase = vuex.getters.getRPSCaseByCaseNumber(caseNumber);

    const rpsChoices = vuex.getters.getRPSCaseChoicesOfRPSCaseInCurrentLocale(
      rpsCase.rpsCase
    );

    for (let choices = 1; choices <= rpsChoices.length; choices++) {
      choicesSheet.addRow([caseNumber, choices, rpsChoices[choices - 1].choice_text]);
    }
  }
}

function generatePlayerChoicesSheet(workbook, vuex) {
  const playerChoicesSheet = workbook.addWorksheet('Player choices');

  playerChoicesSheet.addRow(generateHeaderRow(vuex));

  const players = vuex.getters.getPlayers;

  players.forEach(player => {
    const row = [];
    row.push(player.nick_name)

    for (let j = 1; j <= vuex.getters.getNumberOfRPSCases; j++) {
      const playerAnswer = vuex.getters.getSomePlayersRPSAnswerForCase(player.id, j)

      if (playerAnswer) {
        const choice = vuex.getters.getRPSCaseChoiceByIdInCurrentLocale(playerAnswer.answer);
        row.push(choice.choice_text);
      } else {
        // Has not answered this case
        row.push("");
      }
   }

   playerChoicesSheet.addRow(row);
  })
}


function generateSelfEvaluationsSheet(workbook, vuex) {
  const selfEvaluationsSheet = workbook.addWorksheet('Self evaluations');

  const players = vuex.getters.getPlayers;

  selfEvaluationsSheet.addRow(generateHeaderRow(vuex));

  players.forEach(player => {
    const row = [];
    row.push(player.nick_name)

    for (let j = 1; j <= vuex.getters.getNumberOfCases; j++) {
      const playerEvaluation = vuex.getters.getSomePlayersSelfEvaluationsForCase(player.id, j)

      if (playerEvaluation) {
        const priority = vuex.getters.getNumberOfCases - playerEvaluation.priority + 1;
        row.push(priority);
      } else {
        row.push("");
      }
   }

   selfEvaluationsSheet.addRow(row);
  })
}

function generateNominationsSheet(workbook, vuex) {
  const nominationsSheet = workbook.addWorksheet('Nominations');

  nominationsSheet.addRow(generateHeaderRow(vuex));

  let players = [];

  if (vuex.getters.getIsFacilitator) {
    players = vuex.getters.getPlayers;
  } else {
    // Include only this player's nominations
    players.push(vuex.getters.getPlayerById(vuex.state.main.thisPlayerId))
  }

  players.forEach(player => {
    const row = [];
    row.push(player.nick_name)

    for (let j = 1; j <= vuex.getters.getNumberOfCases; j++) {
      const playerNomination = vuex.getters.getSomePlayersNominationForCase(player.id, j)

      if (playerNomination) {
        const { nominee } = playerNomination;
        const nomineePlayer = vuex.getters.getPlayerById(nominee)

        row.push(nomineePlayer.nick_name);
      } else {
        row.push("");
      }
   }

   nominationsSheet.addRow(row);
  })
}

function generateCommentsSheet(workbook, vuex) {
  const commentsSheet = workbook.addWorksheet('Comments');

  const cases = vuex.getters.getCasesInCurrentLocale;

  cases.forEach(aCase => {
    const row = [];
    row.push(aCase.caseNumber)
    row.push(aCase.shortName)
    commentsSheet.addRow(row);

    const comments = vuex.getters.getCommentsByCaseNumber(aCase.caseNumber);

    comments.forEach(comment => {
      const commentRow = [];
      commentRow.push(comment.comment);
      commentsSheet.addRow(commentRow);
    })
  })
}

function generateSuggestionsSheet(workbook, vuex) {
  const suggestionsSheet = workbook.addWorksheet('Ideas and Suggestions');

  const { suggestions } = vuex.state.main;

  suggestions.forEach(suggestion => {
    const row = [];
    row.push(suggestion.suggestion)
    suggestionsSheet.addRow(row)
  })
}

function generateSurveySheet(workbook, vuex, survey1Averages, survey2Averages) {
  const surveysSheet = workbook.addWorksheet('Surveys');

  const headerRow = [];
  headerRow.push("Case number");
  headerRow.push("Case");
  headerRow.push("Survey question 1");
  headerRow.push("Question 1 average");
  headerRow.push("Survey question 2");
  headerRow.push("Question 2 average");
  surveysSheet.addRow(headerRow);
  const cases = vuex.getters.getCasesInCurrentLocale;


  cases.forEach(aCase => {
    const row = [];
    row.push(aCase.caseNumber)
    row.push(aCase.shortName)

    row.push(getSurveyQuestion(1, aCase.caseNumber, vuex));

    row.push(survey1Averages[aCase.caseNumber]);

    row.push(getSurveyQuestion(2, aCase.caseNumber, vuex));
    row.push(survey2Averages[aCase.caseNumber]);

    surveysSheet.addRow(row);
  })
}

// Header row for self evaluations and nominations and rps player choices
function generateHeaderRow(vuex) {
  const headerRow = [];
  headerRow.push("Nick name");

  const cases = vuex.getters.getIsClassic ? vuex.getters.getNumberOfCases : vuex.getters.getNumberOfRPSCases;

  for (let i = 1; i <= cases; i++) {
     headerRow.push("Case " + i);
  }

  return headerRow;
}
